import { useAssetUpload } from "./useAssetUpload";
import { Button } from "../ui/Button";
import { Modal } from "../ui/Overlay";
import { Notice } from "../ui/Notice";
import { useT } from "../../lib/i18n";
import { putCellAssetApi } from "../../lib/api";
import { cellKey, type Level } from "../../lib/model";
import { Thumb } from "../ui/Thumb";
import { ApiTag } from "../apilens/ApiLens";

export interface AssetCell { b: number; l: number; name: string }

type Kind = "img" | "anim" | "damaged";

/** 接口视角：每种素材下发到 cities:* 块的哪个字段 */
const KIND_API: Record<Kind, string> = { img: "building.img", anim: "building.anim", damaged: "building.damagedImg" };

/**
 * 上传素材：一个格子（关卡 · 建筑 · 星级）对应图片与动画两份资源。
 *
 * **即时保存，不跟「保存」按钮走。** 文件一旦传上去就已经在 CDN 上了，
 * 让这个动作停留在未保存状态没有意义——用户会以为传丢了。所以上传成功后
 * 立刻写库并推进 revision，与数值编辑的批量保存是两条路。
 *
 * 上传走通用件 useAssetUpload（受理格式/大小、目标锁定、busy/错误都在里面），
 * 落库存完整 URL 而不是对象键，与 cfg_card_pack.cover_img 一致。
 */
export function AssetDialog({
  lv, cell, version, revision, editable, onRevision, onLocal, onClose,
}: {
  lv: Level;
  cell: AssetCell;
  version: number;
  revision: number;
  editable: boolean;
  /** 写库成功后推进版本 revision（CAS 串行，父组件持有权威值） */
  onRevision: (next: number) => void;
  /** 同步到本地模型，省得为一张图整关重拉 */
  onLocal: (key: string, kind: Kind, url: string) => void;
  onClose: () => void;
}) {
  const t = useT();
  const key = cellKey(cell.b, cell.l);
  const cur = lv.assets[key] ?? { img: "", anim: "", damaged: "" };
  const base = `lv${lv.id}-b${cell.b}-s${cell.l}`;
  const up = useAssetUpload<Kind>({ module: "cell" });
  const { busy } = up;
  const err = up.error;

  const write = async (kind: Kind, url: string) => {
    const r = await putCellAssetApi(version, lv.id, cell.b, cell.l, revision,
      kind === "img" ? { imgUrl: url } : kind === "anim" ? { animUrl: url } : { damagedImgUrl: url });
    onRevision(r.revision);
    onLocal(key, kind, url);
  };

  const clear = (kind: Kind) => void up.run(() => write(kind, ""));

  const slots: Array<{ k: Kind; title: string; hint: string; url: string }> = [
    { k: "img", title: t("asset.image"), hint: t("asset.imageHint"), url: cur.img },
    { k: "anim", title: t("asset.anim"), hint: t("asset.animHint"), url: cur.anim },
    { k: "damaged", title: t("asset.damaged"), hint: t("asset.damagedHint"), url: cur.damaged },
  ];

  return (
    <Modal
      title={t("asset.title", { id: lv.id, name: cell.name, star: cell.l })}
      width={560}
      onClose={onClose}
      footer={<Button tone="primary" onClick={onClose}>{t("common.done")}</Button>}
    >
      <div className="flex gap-4">
        {slots.map((s) => (
          <div key={s.k} data-api={KIND_API[s.k]} className="flex-1">
            <div className="mb-1 text-sm font-semibold text-ink">{s.title} <ApiTag id={KIND_API[s.k]} /></div>
            <button
              type="button"
              disabled={!editable || busy}
              onClick={() => up.pick(s.k, (url, kind) => write(kind, url))}
              className="flex h-[150px] w-full items-center justify-center overflow-hidden rounded-base border border-dashed border-line bg-fill-light text-sm text-ink-placeholder disabled:cursor-not-allowed"
            >
              {/* 有图：点图看大图，替换走下面那个按钮；没图：整块仍是「点击上传」 */}
              {s.url
                ? <Thumb src={s.url} alt={s.title} title={s.title} className="max-h-full max-w-full object-contain" />
                : <span>{editable ? t("asset.empty") : t("asset.emptyReadonly")}</span>}
            </button>
            <div className="mt-1 flex items-center gap-2">
              <Button size="sm" disabled={!editable || busy} onClick={() => up.pick(s.k, (url, kind) => write(kind, url))}>
                {s.url ? t("asset.replace") : t("asset.upload")}
              </Button>
              {s.url && editable && (
                <Button size="sm" tone="danger" disabled={busy} onClick={() => clear(s.k)}>{t("asset.clear")}</Button>
              )}
            </div>
            <div className="mt-1 text-xs text-ink-placeholder">{s.hint}</div>
            <div className="mt-0.5 font-mono text-xs text-ink-placeholder">{base}-{s.k}</div>
          </div>
        ))}
      </div>

      {up.input}

      {busy && <div className="mt-2 text-sm text-ink-placeholder">{t("asset.uploading")}</div>}
      {err && <div className="mt-2"><Notice tone="danger">{err}</Notice></div>}

      <div className="mt-3">
        <Notice tone="info">{t("asset.notice", { base })}</Notice>
      </div>
    </Modal>
  );
}
